import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  Text,
  Box,
  Button,
  HStack,
  Divider,
  Input,
  useToast,
} from '@chakra-ui/react';
import api from '../services/api';
import { formatUsdFromCents, formatUsdFromDollars, parseUsdInputToDollars } from '../utils/formatCurrency';

const InsufficientCreditsContext = createContext();

export const useInsufficientCredits = () => {
  const context = useContext(InsufficientCreditsContext);
  if (!context) {
    throw new Error('useInsufficientCredits must be used within InsufficientCreditsProvider');
  }
  return context;
};

function normalizeInsufficientPayload(raw) {
  if (raw == null) {
    return { message: 'You do not have enough credits for this operation.', code: null, extra: {} };
  }
  if (typeof raw === 'string') {
    return { message: raw, code: null, extra: {} };
  }
  if (typeof raw === 'object') {
    const nested = raw.detail != null && typeof raw.detail === 'object' && !Array.isArray(raw.detail)
      ? raw.detail
      : null;
    const src = nested || raw;
    const msg =
      src.message ||
      raw.message ||
      raw.detail ||
      'You do not have enough credits for this operation.';
    const code = src.code ?? raw.code ?? null;
    const extra = { ...raw, ...(nested || {}) };
    return { message: typeof msg === 'string' ? msg : String(msg), code, extra };
  }
  return { message: String(raw), code: null, extra: {} };
}

const CAP_CLOSE_AFTER_SAVE = 'cap_saved';

function stripResumeHandlers(payload) {
  if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
    return { cleanPayload: payload, onCapSaved: null, onDismissWithoutResume: null };
  }
  const { onCapSaved, onDismissWithoutResume, ...rest } = payload;
  return {
    cleanPayload: rest,
    onCapSaved: typeof onCapSaved === 'function' ? onCapSaved : null,
    onDismissWithoutResume: typeof onDismissWithoutResume === 'function' ? onDismissWithoutResume : null,
  };
}

export const InsufficientCreditsProvider = ({ children, userSubscriptionStatus }) => {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [blockCode, setBlockCode] = useState(null);
  const [blockExtra, setBlockExtra] = useState({});
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [capInputUsd, setCapInputUsd] = useState('');
  const [capSaving, setCapSaving] = useState(false);

  const onCapSavedRef = useRef(null);
  const onDismissWithoutResumeRef = useRef(null);

  const closeModal = useCallback((closeReason) => {
    if (closeReason !== CAP_CLOSE_AFTER_SAVE && onDismissWithoutResumeRef.current) {
      try {
        onDismissWithoutResumeRef.current();
      } catch (e) {
        console.error(e);
      }
    }
    onCapSavedRef.current = null;
    onDismissWithoutResumeRef.current = null;
    setIsOpen(false);
    setErrorMessage('');
    setBlockCode(null);
    setBlockExtra({});
    setCapInputUsd('');
  }, []);

  const showInsufficientCreditsModal = useCallback((payload = null) => {
    const { cleanPayload, onCapSaved, onDismissWithoutResume } = stripResumeHandlers(payload);
    onCapSavedRef.current = onCapSaved;
    onDismissWithoutResumeRef.current = onDismissWithoutResume;
    const norm = normalizeInsufficientPayload(cleanPayload);
    setErrorMessage(norm.message);
    setBlockCode(norm.code);
    setBlockExtra(norm.extra);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const monthlyCents =
      blockExtra.subscription_monthly_price_cents ??
      userSubscriptionStatus?.subscription_monthly_price_cents ??
      0;
    const capCents = Number(blockExtra.credit_overage_spend_cap_cents) || 0;
    const halfSubDollars = 0.5 * (monthlyCents / 100);
    const doubleCapDollars = 2 * (capCents / 100);
    let suggestion = Math.max(halfSubDollars, doubleCapDollars, 1);

    if (blockCode === 'overage_cap_exceeded') {
      const spent = Number(blockExtra.credit_overage_spend_cents_this_period) || 0;
      const req = Number(blockExtra.required_overage_spend_cents) || 0;
      const minFitDollars = (spent + req) / 100;
      suggestion = Math.max(suggestion, minFitDollars, capCents / 100 + 0.01);
      setCapInputUsd(formatUsdFromDollars(Math.ceil(suggestion * 100) / 100));
    } else if (blockCode === 'overage_disabled') {
      const spent = Number(blockExtra.credit_overage_spend_cents_this_period) || 0;
      const req = Number(blockExtra.required_overage_spend_cents) || 0;
      const minFitDollars = (spent + req) / 100;
      suggestion = Math.max(suggestion, minFitDollars, 50);
      setCapInputUsd(formatUsdFromDollars(Math.ceil(suggestion)));
    } else {
      setCapInputUsd('');
    }
  }, [isOpen, blockCode, blockExtra, userSubscriptionStatus]);

  const currentTierPre = userSubscriptionStatus?.subscription || 'free';
  const paidTiersPre = ['starter', 'basic', 'pro'];
  const isPaidPre = paidTiersPre.includes(currentTierPre);
  const balPre = typeof blockExtra.balance === 'number' ? blockExtra.balance : null;
  const needPre = typeof blockExtra.credits_needed === 'number' ? blockExtra.credits_needed : null;
  const needsMoreIncludedThanBalancePre =
    balPre == null || needPre == null || needPre > balPre;
  const showCapForm =
    isPaidPre &&
    needsMoreIncludedThanBalancePre &&
    (blockCode === 'overage_disabled' || blockCode === 'overage_cap_exceeded');

  const minCapCentsForOperation = useMemo(() => {
    if (!showCapForm) return null;
    const reqRaw = blockExtra.required_overage_spend_cents;
    if (typeof reqRaw !== 'number' || Number.isNaN(reqRaw) || reqRaw < 0) return null;
    const spentRaw = blockExtra.credit_overage_spend_cents_this_period;
    const spent =
      typeof spentRaw === 'number' && !Number.isNaN(spentRaw) ? Math.max(0, spentRaw) : 0;
    return spent + reqRaw;
  }, [showCapForm, blockExtra.required_overage_spend_cents, blockExtra.credit_overage_spend_cents_this_period]);

  const parsedCapCents = useMemo(() => {
    const d = parseUsdInputToDollars(capInputUsd);
    if (Number.isNaN(d) || String(capInputUsd).trim() === '') return null;
    return Math.round(d * 100);
  }, [capInputUsd]);

  const capMeetsOperationMinimum =
    minCapCentsForOperation == null ||
    (parsedCapCents != null && parsedCapCents >= minCapCentsForOperation);

  const handleSaveSpendCap = useCallback(async () => {
    const dollars = parseUsdInputToDollars(capInputUsd);
    if (!(dollars >= 0) || Number.isNaN(dollars)) {
      toast({ title: 'Invalid amount', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (minCapCentsForOperation != null) {
      const cents = Math.round(dollars * 100);
      if (cents < minCapCentsForOperation) {
        toast({
          title: 'Cap too low for this action',
          description: `Set your monthly cap to at least ${formatUsdFromCents(minCapCentsForOperation)} so this period’s spend plus this operation fits under the cap.`,
          status: 'warning',
          duration: 6000,
          isClosable: true,
        });
        return;
      }
    }
    setCapSaving(true);
    try {
      await api.patch('/api/payments/credit-overage-cap', {
        credit_overage_spend_cap_cents: Math.round(dollars * 100),
      });
      const resume = onCapSavedRef.current;
      onDismissWithoutResumeRef.current = null;
      onCapSavedRef.current = null;
      setCapSaving(false);
      toast({
        title: 'Spend cap updated',
        description: resume ? 'Continuing your operation…' : 'You can retry your operation.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      window.dispatchEvent(new Event('subscriptionUpgraded'));
      closeModal(CAP_CLOSE_AFTER_SAVE);
      if (resume) {
        void (async () => {
          try {
            await resume();
          } catch (e) {
            console.error(e);
            toast({
              title: 'Could not complete operation',
              description: e.response?.data?.detail || e.message || 'Try again from the page.',
              status: 'error',
              duration: 6000,
              isClosable: true,
            });
          }
        })();
      }
    } catch (e) {
      console.error(e);
      toast({
        title: 'Could not save cap',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCapSaving(false);
    }
  }, [capInputUsd, closeModal, toast, minCapCentsForOperation]);

  const handleSubscribe = useCallback(async (productKey) => {
    setIsUpgrading(true);
    try {
      const currentTier = userSubscriptionStatus?.subscription || 'free';
      const isNewSubscription = currentTier === 'free';

      if (isNewSubscription) {
        const currentUrl = window.location.href;
        const successUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}payment_success=true`;
        const cancelUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}payment_canceled=true`;

        const response = await api.post('/api/payments/create-subscription-session', {
          product_key: productKey,
          success_url: successUrl,
          cancel_url: cancelUrl,
        });

        window.location.href = response.data.url;
      } else {
        const response = await api.post('/api/payments/change-subscription', {
          product_key: productKey,
        });

        const { status, change_type } = response.data;

        if (status === 'success') {
          if (change_type === 'upgrade') {
            window.dispatchEvent(new Event('subscriptionUpgraded'));
          } else {
            window.dispatchEvent(new Event('subscriptionDowngraded'));
          }

          setTimeout(() => {
            setIsUpgrading(false);
            closeModal();
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error with subscription:', error);
      setIsUpgrading(false);
    }
  }, [userSubscriptionStatus, closeModal]);

  const currentTier = currentTierPre;
  const isPaid = isPaidPre;
  const bal = balPre;
  const need = needPre;
  const needsMoreIncludedThanBalance = needsMoreIncludedThanBalancePre;

  const upgradeTiers = [
    {
      productKey: 'starter_subscription',
      tier: 'starter',
      title: 'Starter',
      price: '$200 / month',
      credits: '3,500 credits / month',
    },
    {
      productKey: 'basic_subscription',
      tier: 'basic',
      title: 'Basic',
      price: '$800 / month',
      credits: '20,000 credits / month',
    },
    {
      productKey: 'pro_subscription',
      tier: 'pro',
      title: 'Pro',
      price: '$3,000 / month',
      credits: '100,000 credits / month',
    },
  ];
  const tierRank = { free: 0, starter: 1, basic: 2, pro: 3 };
  const rankCurrent = tierRank[currentTier] ?? 0;
  const higherTiers = upgradeTiers.filter((t) => (tierRank[t.tier] ?? 0) > rankCurrent);

  const overageRateCents =
    typeof blockExtra.overage_price_per_credit_cents === 'number'
      ? blockExtra.overage_price_per_credit_cents
      : typeof blockExtra.overage_dollars_per_credit === 'number'
        ? Math.round(Number(blockExtra.overage_dollars_per_credit) * 100)
        : null;

  const upgradeButtonProps = {
    bg: '#fafafa',
    color: '#0a0a0a',
    borderRadius: 'full',
    fontWeight: '600',
    fontSize: '13px',
    h: '36px',
    w: '100%',
    _hover: { opacity: 0.85 },
    isLoading: isUpgrading,
    loadingText: 'Upgrading...',
  };

  return (
    <InsufficientCreditsContext.Provider value={{ showInsufficientCreditsModal }}>
      {children}

      <Modal isOpen={isOpen} onClose={closeModal} isCentered size="lg">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0a0a0a" color="#fafafa" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
          <ModalHeader fontSize="lg" fontWeight="700" letterSpacing="-0.01em" pb={3}>
            Insufficient Credits
          </ModalHeader>
          <ModalCloseButton
            color="#a3a3a3"
            _hover={{ color: '#fafafa' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
          />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="#a3a3a3">
                {errorMessage}
              </Text>

              {showCapForm && (
                <Box p={4} borderRadius="10px" bg="rgba(255,255,255,0.02)" border="1px solid rgba(255,255,255,0.08)">
                  <VStack spacing={3} align="stretch">
                    <Text fontSize="sm" fontWeight="600" color="#fafafa">
                      {blockCode === 'overage_cap_exceeded'
                        ? 'Increase your monthly overage spend cap'
                        : 'Enable pay-as-you-go with a spend cap'}
                    </Text>
                    {overageRateCents != null && !Number.isNaN(overageRateCents) && (
                      <Text fontSize="xs" color="rgba(255,255,255,0.45)">
                        Overage rate: {formatUsdFromCents(overageRateCents)} per credit
                      </Text>
                    )}
                    {typeof blockExtra.credit_overage_spend_cents_this_period === 'number' &&
                      typeof blockExtra.credit_overage_spend_cap_cents === 'number' && (
                        <Text fontSize="xs" color="rgba(255,255,255,0.45)">
                          This period: $
                          {(blockExtra.credit_overage_spend_cents_this_period / 100).toFixed(2)} spent of $
                          {(blockExtra.credit_overage_spend_cap_cents / 100).toFixed(2)} cap (
                          {(
                            (blockExtra.credit_overage_spend_cap_cents -
                              blockExtra.credit_overage_spend_cents_this_period) /
                            100
                          ).toFixed(2)}{' '}
                          remaining).
                        </Text>
                      )}
                    {minCapCentsForOperation != null && (
                      <Text fontSize="xs" color="rgba(255,255,255,0.45)">
                        Minimum cap to run this action:{' '}
                        <Text as="span" color="#fafafa" fontWeight="600">
                          {formatUsdFromCents(minCapCentsForOperation)}
                        </Text>{' '}
                        (this period&apos;s spend + this action&apos;s overage).
                      </Text>
                    )}
                    <HStack spacing={2}>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="$0.00"
                        value={capInputUsd}
                        onChange={(e) => setCapInputUsd(e.target.value)}
                        onBlur={() => {
                          const d = parseUsdInputToDollars(capInputUsd);
                          if (!Number.isNaN(d) && String(capInputUsd).trim() !== '') {
                            setCapInputUsd(formatUsdFromDollars(d));
                          }
                        }}
                        bg="rgba(255,255,255,0.04)"
                        borderColor={
                          minCapCentsForOperation != null && !capMeetsOperationMinimum
                            ? 'rgba(239,68,68,0.55)'
                            : 'rgba(255,255,255,0.12)'
                        }
                        color="#fafafa"
                        size="sm"
                      />
                      <Button
                        {...upgradeButtonProps}
                        w="auto"
                        px={5}
                        onClick={handleSaveSpendCap}
                        isLoading={capSaving}
                        loadingText="Saving"
                        isDisabled={
                          capSaving || (minCapCentsForOperation != null && !capMeetsOperationMinimum)
                        }
                      >
                        Save cap
                      </Button>
                    </HStack>
                    <Button
                      variant="ghost"
                      size="sm"
                      color="rgba(255,255,255,0.45)"
                      onClick={() => {
                        window.location.href = '/billing';
                      }}
                    >
                      Open Billing page
                    </Button>
                  </VStack>
                </Box>
              )}

              {showCapForm && higherTiers.length > 0 && (
                <Text fontSize="sm" color="#a3a3a3" fontWeight="600">
                  Or upgrade for more included credits
                </Text>
              )}

              {!showCapForm && currentTier === 'pro' && blockCode !== 'overage_disabled' && blockCode !== 'overage_cap_exceeded' && (
                <Text fontSize="sm" color="#a3a3a3">
                  You&apos;re on the highest tier. Your credits will renew at the start of your next billing period.
                </Text>
              )}

              {!showCapForm && currentTier !== 'pro' && higherTiers.length > 0 && (
                <Text fontSize="sm" color="#a3a3a3">
                  Upgrade your plan to get more monthly credits:
                </Text>
              )}

              {higherTiers.map((t) => (
                <Box
                  key={t.productKey}
                  p={4}
                  borderRadius="10px"
                  bg="rgba(255,255,255,0.02)"
                  border="1px solid rgba(255,255,255,0.08)"
                >
                  <VStack spacing={3} align="stretch">
                    <HStack justify="space-between">
                      <Text fontWeight="600" color="#fafafa" fontSize="sm">
                        {t.title}
                      </Text>
                      <Text fontWeight="500" color="#a3a3a3" fontSize="sm">
                        {t.price}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="rgba(255,255,255,0.35)">
                      {t.credits}
                    </Text>
                    <Button {...upgradeButtonProps} onClick={() => handleSubscribe(t.productKey)}>
                      Upgrade to {t.title}
                    </Button>
                  </VStack>
                </Box>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter pt={2}>
            <Button
              variant="ghost"
              color="rgba(255,255,255,0.5)"
              borderRadius="full"
              fontSize="sm"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.05)' }}
              onClick={closeModal}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </InsufficientCreditsContext.Provider>
  );
};

export default InsufficientCreditsContext;
